import React, { useState } from "react";

import { useForm, SubmitHandler } from "react-hook-form";
import Input from "components/atoms/Input/Input"; // Custom Input component
import { Layout } from "components/Layout.tsx/Layout";
import Button from "components/atoms/Button/Button";
import { useAxiosPrivate } from "hooks/api/useAxiosPrivate";
import { useMutation } from "react-query";
import { Footer } from "components/Footer/Footer";
import { Modal } from "components/Modal/Modal";
import ErrorModalContent from "components/ErrorModalContent/ErrorModalContent";
import SuccessModalContent from "components/SuccessModalContent/SuccessModalContent";

import {
  ButtonWrapper,
  Container,
  ErrorText,
  Form,
  Label,
  Subtitle,
  Title,
} from "./styles";

import { useAuth } from "context/AuthContext";

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const Profile: React.FC = () => {
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState<{
    [field: string]: string;
  }>({});

  const { clearAuth } = useAuth();

  const axiosPrivate = useAxiosPrivate();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>();

  // Watch new password to validate confirmPassword field
  const newPassword = watch("newPassword");

  const changePasswordMutation = useMutation(
    (passwordData: any) => {
      return axiosPrivate.post(`/change-password`, passwordData);
    },
    {
      onSuccess: (data) => {
        setSuccessModalOpen(true);
      },
      onError: (error) => {
        const err: any = error;
        if (err.response && err.response.data && err.response.data.error) {
          try {
            const parsedError = JSON.parse(err.response.data.error);

            if (parsedError.errors) {
              setErrorMessages(parsedError.errors);
            } else {
              setErrorMessages({ "error details": parsedError });
            }
          } catch (parseError) {
            setErrorMessages({ "error details": err.response.data.error });
          }
        } else {
          setErrorMessages({ "error details": "An unknown error occurred." });
        }

        setErrorModalOpen(true);
      },
    }
  );

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const { currentPassword, newPassword } = data;

    changePasswordMutation.mutate({
      current_password: currentPassword,
      new_password: newPassword,
    });
  };

  return (
    <Layout>
      <Container>
        <Title>Change Password</Title>
        <Subtitle>
          Ensure your account is using a solid password to stay secure.
        </Subtitle>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <Label>
            Current Password
            <Input
              type="password"
              placeholder="Enter your current password"
              {...register("currentPassword", {
                required: "Current password is required",
              })}
            />
            {errors.currentPassword && (
              <ErrorText>{errors.currentPassword.message}</ErrorText>
            )}
          </Label>

          <Label>
            New Password
            <Input
              type="password"
              placeholder="Enter your new password"
              {...register("newPassword", {
                required: "New password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
                pattern: {
                  value:
                    /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&^#()_+={}\[\]:;"'<>,.~`-]{6,}$/,
                  message: "Password must contain both letters and numbers",
                },
              })}
            />
            {errors.newPassword && (
              <ErrorText>{errors.newPassword.message}</ErrorText>
            )}
          </Label>

          <Label>
            Confirm New Password
            <Input
              type="password"
              placeholder="Confirm your new password"
              {...register("confirmPassword", {
                required: "Please confirm your new password",
                validate: (value) =>
                  value === newPassword || "Passwords do not match",
              })}
            />
            {errors.confirmPassword && (
              <ErrorText>{errors.confirmPassword.message}</ErrorText>
            )}
          </Label>

          <ButtonWrapper>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Changing..." : "Change Password"}
            </Button>
          </ButtonWrapper>
        </Form>
      </Container>

      {errorModalOpen && (
        <Modal
          onClose={() => {
            setErrorModalOpen(false);
          }}
        >
          <ErrorModalContent
            errorMessages={errorMessages}
            onClose={() => {
              setErrorModalOpen(false);
            }}
          />
        </Modal>
      )}
      {successModalOpen && (
        <Modal onClose={() => setSuccessModalOpen(false)}>
          <SuccessModalContent
            successMessage={"Password Changed Successfully!"}
            onClose={() => clearAuth()}
          />
        </Modal>
      )}
      <Footer />
    </Layout>
  );
};
